// NodeButtons.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useNodeWorkflow } from 'context/NodeWorkflowContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { resolveBaseKey } from 'utilities/basePersistence';
import { EngagementType, SaveMode, SectionType } from 'utilities/Enums';
import { canUser, PermissionAction } from 'utilities/Permissions';
import { buildNodeQRCodeUrl } from 'utilities/QRCode';
import { getSectionPath } from 'utilities/Routing';

// Services
import { addEngagement, deleteEngagement } from 'services/engagements/EngagementWriteService';
import { moveNodeToTrash } from 'services/nodes/NodeWriteService';
import {
  downloadNodeMedia,
  isDownloadableMediaNode,
} from 'services/nodes/nodeMediaDownload';
import {
  discardProvisional,
  getProvisionalSession,
  isProvisional,
} from 'node/provisional/provisionalNodeSession';
import { toast } from 'react-toastify';

// Hooks
import useBaseAccess from 'hooks/useBaseAccess';

// CSS
import './NodeButtons.css';

// Components
import Confirm from 'components/confirm/Confirm';
import MenuButton from 'components/menubutton/MenuButton';
import MenuPanel from 'components/menupanel/MenuPanel';
import PrintPreviewModal from 'components/printpreviewmodal/PrintPreviewModal';
import NodeShareModal from 'nodeshare/NodeShareModal';

const PIN_DROP_PATH = 'M480-276q109-83 164.5-169.5T700-600q0-97-61.5-158.5T480-820q-97 0-158.5 61.5T260-600q0 67 55.5 153.5T480-276Zm0 76Q338-303 269-406t-69-194q0-123 78.5-201.5T480-880q123 0 201.5 78.5T760-600q0 91-69 194T480-200Zm-1-320q34 0 57.5-23.5T560-600q0-33-23.5-56.5T479-680q-33 0-56 23.5T400-600q0 33 23 56.5t56 23.5ZM200-80v-60h560v60H200Zm280-520Z';
const PIN_DROP_FILL_PATH = 'M480-200Q338-303 269-406t-69-194q0-123 78.5-201.5T480-880q123 0 201.5 78.5T760-600q0 91-69 194T480-200Zm-1-320q34 0 57.5-23.5T560-600q0-33-23.5-56.5T479-680q-33 0-56 23.5T400-600q0 33 23 56.5t56 23.5ZM200-80v-60h560v60H200Z';

/**
 * NodeButtons
 *
 * Renders action buttons for a node: pin, copy link, QR code, print, and delete.
 *
 * @param {object} node - The node object for which to render buttons.
 * @param {boolean} navigateOnDelete - Whether to navigate home after a successful delete.
 * @param {function} onNodeDeleted - Called after the node is deleted successfully.
 * @param {'default'|'chrome'} [variant='default'] - Button chrome style (Peek uses chrome).
 *
 * @returns {ReactNode} NodeButtons.
 */
const NodeButtons = React.memo(({
    navigateOnDelete = true,
    node,
    onNodeDeleted,
    variant = 'default',
}) => {

    // ----- HOOKS ----- //

    const { setEditorSaveMode, setNodes } = useNodeWorkflow();
    // ----- CONTEXT ----- //

    const { setCurrentNode, setCurrentSection } = useNavigation();
    const { newUserProfileSetup, setNewUserProfileSetup } = useUI();
    const { theme } = useTheme();

    // User Context
    const {
        user,
        userEngagements,
    } = useUser();
    const { activeBase } = useBase();

    // Node Type Context
    const { getLabel } = useNodeTypes();
    const language = useCurrentLanguage();

    // ----- HOOKS ----- //

    const { can, isRoleLoading } = useBaseAccess(activeBase);

    // ----- ROUTING ----- //

    const navigate = useNavigate();

    const nodeKey = node?.key;

    // ----- MEMOS ----- //

    const canDownloadMedia = useMemo(
      () => isDownloadableMediaNode(node),
      [node],
    );

    const nodeTypeLabel = useMemo(() => getLabel(node?.type, language), [getLabel, node?.type, language]);

    const favorited = useMemo(() =>
        nodeKey && userEngagements ? userEngagements.some(engagement =>
            engagement.itemKey === nodeKey &&
            engagement.type === EngagementType.NODE_FAVORITE) : false,
        [userEngagements, nodeKey]
    );

    const isNodeOwner = node?.userKey === user?.key;

    const canDelete = useMemo(() => {
      if (!node || !user) return false;
      // Platform catalog pointers cannot be deleted from the workspace.
      if (String(node.catalogId || '').trim()) return false;
      return isNodeOwner || (!isRoleLoading && can('node.deleteAny'));
    }, [can, isNodeOwner, isRoleLoading, node, user]);

    const deleteConfirmTitle = 'Move to Trash';
    const deleteConfirmMessage = `This will move this ${nodeTypeLabel.toLowerCase()} to the Recycle Bin. You can restore it later or permanently delete it from the Recycle Bin.`;

    const canCopyOrShowQr = useMemo(() =>
        node && user ? canUser(PermissionAction.SHARE, node, user) : false,
        [node, user]
    );

    // ----- STATE ----- //

    // State

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // QR Code
    const [qrOpen, setQrOpen] = useState(false);

    // Print Preview
    const [printPreviewOpen, setPrintPreviewOpen] = useState(false);

    // Download
    const [downloadBusy, setDownloadBusy] = useState(false);

    // ----- EFFECTS ----- //

    // Reposting
    // [editorNode, setEditorNode] = useState(null);
    // [editorOpen, setEditorOpen] = useState(false);

    /**
     * Check for new user profile setup
     */

    useEffect(() => {
        if (newUserProfileSetup && node?.key === user?.key && node?.type === 'USER') {
            setEditorSaveMode(SaveMode.UPDATE);
            setNewUserProfileSetup(false);
        }
    }, [newUserProfileSetup, node, user, setEditorSaveMode, setNewUserProfileSetup]);

    // ----- HANDLERS ----- //

    /**
     * Handles pin button click.
     */

    const handleFavoriteClick = async () => {
        if (!favorited) {
            const favoriteBaseKey = resolveBaseKey(node) || String(activeBase?.key || '').trim();
            addEngagement(
                EngagementType.NODE_FAVORITE,
                node,
                user,
                favoriteBaseKey ? { baseKey: favoriteBaseKey } : {},
            );
        } else {
            deleteEngagement(EngagementType.NODE_FAVORITE, node, user);
        }
    };

    /**
     * Deletes the node (hard-delete when provisional; otherwise trash) and navigates home.
     */
    const handleDeleteConfirm = async () => {
        try {
            const session = getProvisionalSession(node.key);
            const provisionalDelete = isProvisional(node)
                || Boolean(session && !session.committed);

            if (provisionalDelete) {
                await discardProvisional(node.key);
            } else {
                await moveNodeToTrash(node.key, {
                    trashedByKey: user?.key || null,
                });
            }

            setNodes(prevNodes => prevNodes.filter(n => n.key !== node.key));

            if (onNodeDeleted) {
                await onNodeDeleted(node);
            }

            setConfirmDeleteOpen(false);

            if (navigateOnDelete) {
                setCurrentSection(SectionType.HOME);
                setCurrentNode(null);
                navigate(getSectionPath(SectionType.HOME));
            }
        } catch (error) {
            toast.error(error?.message || 'Could not delete this item.', { autoClose: 4000 });
        }
    };

    /**
     * Handles delete button click.
     */
    const handleDeleteClick = () => {
        setConfirmDeleteOpen(true);
    };

    /**
     * Copies the node's absolute URL to the clipboard.
     */
    const handleCopyClick = async () => {
        const shareUrl = buildNodeQRCodeUrl(node?.key);
        if (!shareUrl) return;

        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            toast.error('Copy is not available in this browser.', { autoClose: 3000 });
            return;
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Link copied.', { autoClose: 2000 });
        } catch (error) {
            console.error('Failed to copy node link:', error);
            toast.error('Could not copy link.', { autoClose: 3000 });
        }
    };

    /**
     * Handles print preview button click.
     */
    const handlePrintClick = () => {
        setPrintPreviewOpen(true);
    };

    /**
     * Handles download button click for media nodes.
     */
    const handleDownloadClick = async () => {
        if (!canDownloadMedia || downloadBusy) return;
        setDownloadBusy(true);
        try {
            await downloadNodeMedia(node);
        } finally {
            setDownloadBusy(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <MenuPanel orientation="HORIZONTAL">

                <MenuButton
                  iconKey={favorited ? 'pin_drop_fill' : 'pin_drop'}
                  path={favorited ? PIN_DROP_FILL_PATH : PIN_DROP_PATH}
                  title={`Pin This ${nodeTypeLabel}`}
                  selected={favorited}
                  selectedColor={theme.orange}
                  size={20}
                  variant={variant}
                  onClick={handleFavoriteClick}
                />

                {canDownloadMedia ? (
                    <MenuButton
                      iconKey="download"
                      title={`Download This ${nodeTypeLabel}`}
                      size={20}
                      variant={variant}
                      onClick={handleDownloadClick}
                    />
                ) : null}

                {canCopyOrShowQr ? (
                    <MenuButton
                      iconKey="content_copy"
                      title={`Copy Link to This ${nodeTypeLabel}`}
                      size={20}
                      variant={variant}
                      onClick={handleCopyClick}
                    />
                ) : null}

                {canCopyOrShowQr ? (
                    <MenuButton
                      iconKey="qr_code"
                      title={`QR Code for This ${nodeTypeLabel}`}
                      size={20}
                      variant={variant}
                      onClick={() => setQrOpen(true)}
                    />
                ) : null}

                {node && user && (
                    <>
                        <MenuButton
                          iconKey="print"
                          title={`Print This ${nodeTypeLabel}`}
                          size={20}
                          variant={variant}
                          onClick={handlePrintClick}
                        />

                        {canDelete && (
                            <MenuButton
                              iconKey="delete"
                              title={`Move This ${nodeTypeLabel} to the Recycle Bin`}
                              size={20}
                              variant={variant}
                              onClick={handleDeleteClick}
                            />
                        )}
                    </>
                )}

            </MenuPanel>

            <Confirm
              title={deleteConfirmTitle}
              message={deleteConfirmMessage}
              isOpen={confirmDeleteOpen}
              setOpen={setConfirmDeleteOpen}
              onConfirm={handleDeleteConfirm}
            />

            <NodeShareModal
              node={node}
              isOpen={qrOpen}
              setOpen={setQrOpen}
            />

            <PrintPreviewModal
              isOpen={printPreviewOpen}
              node={node}
              onClose={() => setPrintPreviewOpen(false)}
            />

        </>
    );
});

// Dev Tools
NodeButtons.displayName = 'NodeButtons';

export default NodeButtons;
